import { reactive } from 'vue';

/**
 * DEMANDER CONFIRMATION, DANS LA CHARTE.
 *
 * Sept gestes irréversibles passaient encore par le `confirm()` du navigateur :
 * une boîte grise, posée par le système d'exploitation, dont on ne maîtrise ni
 * la typographie, ni les couleurs, ni même le libellé des boutons — « OK » et
 * « Annuler », en anglais sur certains postes. Au milieu d'un outil charté,
 * elle a l'air d'une erreur de l'application, et c'est précisément au moment le
 * plus délicat — juste avant de détruire quelque chose — qu'on veut inspirer le
 * contraire.
 *
 * L'API RESTE IMPÉRATIVE, exprès. `if (!(await confirmer({...}))) return;` se
 * substitue au `confirm()` d'origine ligne pour ligne. Une fenêtre déclarative
 * aurait demandé, dans chacun des sept fichiers, un état, un gabarit et un
 * gestionnaire — trois occasions de diverger là où il n'y a qu'une question à
 * poser.
 */
export interface DemandeConfirmation {
  /** Titre de la fenêtre : le geste, à l'infinitif. */
  titre: string;
  /** La question, en une ou deux phrases. */
  message: string;
  /**
   * Ce que le geste emporte, ligne par ligne. « Êtes-vous sûr ? » ne veut rien
   * dire tant qu'on ne sait pas de quoi on parle.
   */
  details?: string[];
  /** Le libellé du bouton qui agit. Il NOMME le geste, jamais « OK ». */
  action?: string;
  /**
   * `true` (défaut) pour un geste destructeur : bouton rouge et encadré
   * d'inventaire alarmant. `false` pour un geste réversible — dupliquer,
   * rouvrir —, qui n'a pas à se présenter comme un danger.
   */
  danger?: boolean;
  /** La voie douce, quand elle existe : « pour prévenir sans détruire… ». */
  repli?: string;
}

interface Etat extends DemandeConfirmation {
  ouvert: boolean;
  resoudre: ((ok: boolean) => void) | null;
}

/** Lu par `ConfirmerHote`, monté une seule fois dans la coquille. */
export const etatConfirmation = reactive<Etat>({
  ouvert: false,
  titre: '',
  message: '',
  details: [],
  action: 'Confirmer',
  danger: true,
  repli: '',
  resoudre: null,
});

export function confirmer(demande: DemandeConfirmation): Promise<boolean> {
  // Une demande qui en interrompt une autre annule la précédente : laisser deux
  // promesses en suspens, c'est laisser un appelant attendre pour toujours.
  etatConfirmation.resoudre?.(false);

  etatConfirmation.titre = demande.titre;
  etatConfirmation.message = demande.message;
  etatConfirmation.details = demande.details ?? [];
  etatConfirmation.action = demande.action ?? 'Confirmer';
  etatConfirmation.danger = demande.danger ?? true;
  etatConfirmation.repli = demande.repli ?? '';
  etatConfirmation.ouvert = true;

  return new Promise<boolean>((resolve) => {
    etatConfirmation.resoudre = resolve;
  });
}

/** Referme et rend la réponse. Appelé par l'hôte, et par lui seul. */
export function repondre(ok: boolean): void {
  etatConfirmation.ouvert = false;
  const resoudre = etatConfirmation.resoudre;
  etatConfirmation.resoudre = null;
  resoudre?.(ok);
}
